import React from 'react';
import { Device } from '../../lib/types';
import type { RescueDeviceSortColumn, SortDir } from '../../lib/types';
import { formatTimestamp, formatDaysAgo } from '../../lib/utils';
import ActionMenu, { type ActionMenuEntry } from '../../components/ActionMenu/ActionMenu';
import DeviceCell from './DeviceCell';
import { ACTION_COLUMN_WIDTH } from './columnWidths';
import { SkeletonRows } from '../../components/SkeletonRow/SkeletonRow';
import Pagination from '../../components/Pagination/Pagination';
import CollapsibleSection from './CollapsibleSection';
import TableHead, { type TableColumn } from './TableHead';
import PetNameCell from './PetNameCell';
import styles from './Devices.module.scss';
import { sendSmsEntry } from './sendSmsEntry';

/** Columns the pending table can be sorted by (clicking their header). */
export type PendingSortKey = Extract<RescueDeviceSortColumn, 'adopter' | 'pet' | 'device' | 'transferred'>;

// No Battery column: the reading is stacked in the Device cell (see DeviceCell), and Action
// holds one menu trigger rather than a row of buttons — both so the width goes to the
// adopter, pet and date columns that are actually read at a glance. The lead columns fill
// `100 - ACTION_COLUMN_WIDTH`, so Action lines up with the tables below it on this tab.
export const PENDING_COLUMNS: TableColumn<PendingSortKey>[] = [
  { label: 'Adopter', width: '21%', sortKey: 'adopter' },
  { label: 'Pet', width: '16%', sortKey: 'pet' },
  { label: 'Device', width: '20%', sortKey: 'device' },
  { label: 'Status', width: '16%' },
  { label: 'Transferred', width: '14%', sortKey: 'transferred' },
  { label: 'Action', width: `${ACTION_COLUMN_WIDTH}%`, hideLabel: true },
];

interface PendingTransfersTableProps {
  devices: Device[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  skeletonCount: number;
  sortKey: PendingSortKey | null;
  sortDir?: SortDir;
  onSort: (key: PendingSortKey) => void;
  onResendEmail: (device: Device) => void;
  onCancelTransfer: (device: Device) => void;
  onSendSms: (device: Device) => void;
}

export default function PendingTransfersTable({
  devices,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  loading,
  skeletonCount,
  sortKey,
  sortDir,
  onSort,
  onResendEmail,
  onCancelTransfer,
  onSendSms,
}: PendingTransfersTableProps) {
  if (totalCount === 0 && !loading && skeletonCount === 0) {
    return null;
  }

  const hasData = devices.length > 0;
  const showOverlay = skeletonCount > 0 && hasData;
  const showSkeleton = skeletonCount > 0 && !hasData;
  const wrapperClass = `${styles.tableWrapper}${showOverlay ? ` ${styles.tableTransitioning}` : ''}`;

  // Ways to nudge the adopter first, then the way out. Cancel is separated and coloured
  // because it ends the transfer and prompts for a reason.
  function pendingActions(d: Device): ActionMenuEntry[] {
    return [
      { ...sendSmsEntry(d, 'Adopter'), onSelect: () => onSendSms(d) },
      { label: 'Email', onSelect: () => onResendEmail(d) },
      { label: 'Cancel Transfer', onSelect: () => onCancelTransfer(d), danger: true, dividerBefore: true },
    ];
  }

  return (
    <CollapsibleSection
      title="Pending"
      count={totalCount}
      variant="warning"
      description="Devices being transferred to an adopter — waiting for them to activate."
    >
      <div className={wrapperClass}>
        <table className={styles.table}>
          <TableHead columns={PENDING_COLUMNS} sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
          <tbody>
            {showSkeleton ? (
              <SkeletonRows count={skeletonCount} columns={PENDING_COLUMNS.length} />
            ) : (
              devices.map((d) => {
                const isOnline = d.status === 'online';

                return (
                  <tr key={d.id}>
                    <td>
                      {d.adopterName}
                      <br />
                      <span className={styles.adopterEmail}>{d.adopterEmail}</span>
                    </td>
                    <td>
                      {d.petName ? <PetNameCell name={d.petName} photoThumbUrl={d.petPhotoThumbUrl} /> : '\u2014'}
                    </td>
                    <td>
                      <DeviceCell moduleId={d.id} type={d.type} showBattery battery={d.battery} />
                    </td>
                    <td>
                      {isOnline ? (
                        <>
                          <div className={styles.statusCell}>
                            <span className={`${styles.statusDot} ${styles.statusOnline}`} />
                            Online
                          </div>
                          <div className={styles.lastSeen}>Now</div>
                        </>
                      ) : (
                        <a
                          href="https://support.fitracking.com/hc/en-us/articles/360033210214-Offline-Fi-Device-Troubleshooting"
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Troubleshooting: Offline Fi Device"
                          className={styles.offlineLink}
                        >
                          <div className={styles.statusCell}>
                            <span className={`${styles.statusDot} ${styles.statusOffline}`} />
                            Offline
                          </div>
                          <div className={styles.lastSeen}>{formatTimestamp(d.lastSeen)}</div>
                        </a>
                      )}
                    </td>
                    <td>
                      <span className={styles.transferAge}>{formatDaysAgo(d.transferDate)}</span>
                    </td>
                    <td>
                      <ActionMenu items={pendingActions(d)} ariaLabel={`Actions for ${d.petName ?? d.id}`} />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </CollapsibleSection>
  );
}
